import { useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import db from "../../../../../database";
import usePOSConfigStore from "../../../../../stores/usePOSConfigStore";
import useAuthStore from "../../../../../stores/useAuthStore";
import { syncSingleProductByValue } from "../../../../../services";

/**
 * Encapsulates product search/autocomplete logic for the SuperMarket POS.
 */
const useProductSearch = ({ posConfig, productSearchInput, setProductSearchInput, keyboard, pickProduct, setOptions, debounceRef, enterRef }) => {
  const { t } = useTranslation();
  const [productMap, setProductMap] = useState({});
  const [productsList, setProductsList] = useState([]);

  const handleSearch = async (data) => {
    const config = usePOSConfigStore.getState().posConfig;
    if (!data) {
      setOptions([]);
      return;
    }

    let products = await db.products.search({
      searchTerm: data,
      isSmartSearch: config?.is_smart_product_search === "Y",
      limit: 20,
    });

    // Cache miss → try on-demand single-product sync once (roadmap ad6b7339).
    // Only fire for inputs with at least 2 chars so every keystroke doesn't
    // round-trip the server.
    if (products.length === 0 && String(data).trim().length >= 2) {
      const tillData = useAuthStore.getState().tillData;
      if (tillData?.tillAccess?.csBunit?.csBunitId) {
        try {
          const synced = await syncSingleProductByValue(tillData, uuidv4(), tillData?.tillAccess?.cwrTill?.cwrTillID, String(data).trim(), { ignoreLastSync: true });
          if (synced) {
            products = await db.products.search({
              searchTerm: data,
              isSmartSearch: config?.is_smart_product_search === "Y",
              limit: 20,
            });
          }
        } catch (err) {
          console.warn("[SuperMarket search] single-product sync failed:", err?.message);
        }
      }
    }

    if (products.length > 0) {
      const filteredOptions = products.map((item) => ({
        label: `${item.value} - ${item.name}`,
        value: item.value,
      }));
      const map = {};
      products.forEach((item) => {
        map[item.value] = item;
      });
      setOptions(filteredOptions);
      setProductMap(map);
    } else {
      setOptions([]);
      setProductMap({});
    }
  };

  const onChangeInput = async (event) => {
    const inputVal = event.target.value;
    setProductSearchInput(inputVal);
    localStorage.setItem("productName", inputVal);
    keyboard?.current?.setInput(inputVal);
    if (posConfig?.product_search_display_mode === "dropdown") {
      if (inputVal.trim() === "") {
        setOptions([]);
        setProductMap({});
        return;
      }
      // await handleSearch(inputVal);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        handleSearch(inputVal);
      }, 1000);
    }
  };

  const handleSelectProduct = async () => {
    const products = await db.products.search({
      searchTerm: productSearchInput,
      limit: 500,
    });
    if (products.length === 0) {
      message.info(`${t("product_search_category_error")}`);
    } else {
      setProductsList(products);
    }
  };

  return {
    productMap,
    setProductMap,
    productsList,
    setProductsList,
    handleSearch,
    onChangeInput,
    handleSelectProduct,
  };
};

export default useProductSearch;
